import { Phone, Mail, Globe, Clock, Siren } from "lucide-react";
import PageHero from "@/components/PageHero";
import { Reveal, SectionHeading } from "@/components/Reveal";
import EnquiryForm from "@/components/EnquiryForm";
import { IMAGES, PHONE, PHONE_LINK, EMAIL, EMAIL_LINK, WEBSITE, WEBSITE_LINK, HOURS, EMERGENCY_NOTE } from "@/data/site";

const Contact = () => (
  <div data-testid="contact-page">
    <PageHero
      testId="contact-hero"
      kicker="Contact"
      titleLines={["One call.", "We handle the rest."]}
      description="If you've just had an accident, call us now — the sooner we're involved, the more we can protect you."
      image={IMAGES.night}
    />

    <section className="mx-auto max-w-7xl px-6 py-28 md:px-10 md:py-36">
      <div className="grid grid-cols-1 gap-16 lg:grid-cols-2">
        <div>
          <SectionHeading kicker="Reach Us" title="Every channel. One team." />

          <Reveal delay={0.1}>
            <a
              href={PHONE_LINK}
              data-testid="contact-call-now-button"
              className="group mt-12 flex items-center justify-between rounded-3xl border border-gold/30 bg-gradient-to-br from-graphite to-obsidian p-8 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold md:p-10"
            >
              <div>
                <p className="font-mono text-[10px] uppercase tracking-[0.35em] text-gold">Click To Call</p>
                <p className="mt-3 font-serif text-4xl text-white transition-colors duration-300 group-hover:text-gold md:text-5xl">
                  {PHONE}
                </p>
                <p className="mt-3 text-sm text-zinc-500">Free from UK landlines and mobiles</p>
              </div>
              <span className="flex h-16 w-16 shrink-0 items-center justify-center rounded-full bg-gold transition-transform duration-500 group-hover:scale-110">
                <Phone className="h-6 w-6 text-obsidian" strokeWidth={2} />
              </span>
            </a>
          </Reveal>

          <Reveal delay={0.18}>
            <div className="mt-6 grid grid-cols-1 gap-6 sm:grid-cols-2">
              <a
                href={EMAIL_LINK}
                data-testid="contact-email-card"
                className="group rounded-2xl border border-white/10 bg-carbon p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold/40"
              >
                <Mail className="h-5 w-5 text-gold" strokeWidth={1.5} />
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Email</p>
                <p className="mt-2 break-all text-sm font-medium text-white group-hover:text-gold">{EMAIL}</p>
              </a>
              <a
                href={WEBSITE_LINK}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-website-card"
                className="group rounded-2xl border border-white/10 bg-carbon p-7 transition-[border-color,transform] duration-500 hover:-translate-y-1 hover:border-gold/40"
              >
                <Globe className="h-5 w-5 text-gold" strokeWidth={1.5} />
                <p className="mt-5 font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-500">Website</p>
                <p className="mt-2 text-sm font-medium text-white group-hover:text-gold">{WEBSITE}</p>
              </a>
            </div>
          </Reveal>

          <Reveal delay={0.26}>
            <div className="mt-6 rounded-2xl border border-white/10 bg-carbon p-7 md:p-8" data-testid="contact-hours-card">
              <div className="flex items-center gap-3">
                <Clock className="h-5 w-5 text-gold" strokeWidth={1.5} />
                <h3 className="font-mono text-[10px] uppercase tracking-[0.3em] text-zinc-400">Operating Hours</h3>
              </div>
              <dl className="mt-6 space-y-3">
                {HOURS.map((h) => (
                  <div key={h.days} className="flex items-baseline justify-between gap-4 border-b border-white/5 pb-3">
                    <dt className="text-sm text-zinc-500">{h.days}</dt>
                    <dd className="text-sm font-medium text-white">{h.time}</dd>
                  </div>
                ))}
              </dl>
              <p className="mt-6 flex items-start gap-3 rounded-xl border border-gold/20 bg-gold/5 p-4 text-xs leading-relaxed text-gold/90" data-testid="contact-emergency-note">
                <Siren className="mt-0.5 h-4 w-4 shrink-0" strokeWidth={1.5} />
                {EMERGENCY_NOTE}
              </p>
            </div>
          </Reveal>
        </div>

        <Reveal delay={0.15}>
          <div className="rounded-3xl border border-white/10 bg-carbon p-8 md:p-10" data-testid="contact-form-card">
            <h2 className="font-serif text-3xl text-white">Request Assistance</h2>
            <p className="mt-3 text-sm leading-relaxed text-zinc-500">
              Send the basics and a dedicated specialist will call you back. You'll receive an instant
              confirmation email with your reference.
            </p>
            <div className="mt-10">
              <EnquiryForm />
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  </div>
);

export default Contact;
